import {
  Alert,
  Box,
  Link as MuiLink,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from '@mui/material';
import { GoogleLogin } from '@react-oauth/google';
import { checkAuthPolicy } from 'client/modules/Auth/AuthActions';
import { authenticatePasskey, checkPasskeys } from 'client/modules/Auth/PasskeyActions';
import FormsyPassword from 'components/FormsyPassword/FormsyPassword';
import FormsyText from 'components/FormsyText/FormsyText';
import Message from 'components/Message/Message';
import PasswordStrengthIndicator, {
  validatePassword,
} from 'components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import Formsy from 'formsy-react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import {
  loginWidgetAcceptTermsSx,
  loginWidgetContainerSx,
  loginWidgetForgotSx,
  loginWidgetFormSx,
  loginWidgetHrTextSx,
  loginWidgetSubmitSx,
} from './loginWidgetStyles';

export const loginMessages = defineMessages({
  'Signup.Title': {
    id: 'Signup.Title',
    defaultMessage: 'Sign up',
  },
  'Signup.Submit': {
    id: 'Signup.Submit',
    defaultMessage: 'Create my account',
  },
  'Login.Title': {
    id: 'Login.Title',
    defaultMessage: 'Log in',
  },
  'Login.Submit': {
    id: 'Login.Submit',
    defaultMessage: 'Log in',
  },
  email: {
    id: 'Login.Email',
    defaultMessage: 'Business Email',
  },
  emailPlaceholder: {
    id: 'Login.EmailPlaceholder',
    defaultMessage: 'name@company.com',
  },
  password: {
    id: 'Login.Password',
    defaultMessage: 'password',
  },
  submit: {
    id: 'Login.Submit',
    defaultMessage: 'Log in',
  },
  invalidEmail: {
    id: 'Login.Errors.InvalidEmail',
    defaultMessage: 'This is not a valid email',
  },
  or: {
    id: 'Login.Or',
    defaultMessage: 'or',
  },
  ssoEnforced: {
    id: 'Login.SSOEnforced',
    defaultMessage: 'This organization requires single sign-on. Continue with {providers}.',
  },
  ssoRequiredError: {
    id: 'Login.SSORequiredError',
    defaultMessage: 'This organization requires single sign-on. Sign in with {providers}.',
  },
  yourSSOProvider: {
    id: 'Login.YourSSOProvider',
    defaultMessage: 'your SSO provider',
  },
  deprecatedAccount: {
    id: 'Login.DeprecatedAccount',
    defaultMessage:
      'Access with this email has been disabled. Please use your corporate account to sign in via {providers}.',
  },
  passkeyLoginPrompt: {
    id: 'Login.PasskeyLoginPrompt',
    defaultMessage: 'Attempting passkey authentication...',
  },
  signInWithPasskey: {
    id: 'Login.SignInWithPasskey',
    defaultMessage: 'Sign in with passkey',
  },
  usePasswordInstead: {
    id: 'Login.UsePasswordInstead',
    defaultMessage: 'Use password instead',
  },
});

// FedCM requires the origin to be registered in Google Cloud Console.
// Disable it in local development to avoid CORS/network errors.
const isLocalhost =
  typeof window === 'object' &&
  (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1');

const fedcmSupported =
  !isLocalhost &&
  typeof window === 'object' &&
  'IdentityCredential' in window &&
  (typeof document.permissionsPolicy?.allowsFeature !== 'function' ||
    !!document.permissionsPolicy.allowsFeature('identity-credentials-get'));

let fedcmActiveMode = false;
try {
  if (fedcmSupported && typeof navigator === 'object') {
    navigator.credentials
      .get({
        identity: Object.defineProperty({}, 'mode', {
          get: () => {
            fedcmActiveMode = true;
            return undefined;
          },
        }),
      })
      .catch(() => {});
  }
} catch {
  // FedCM probe not supported in this browser.
}

// Helpers to format provider ids like ["google", "microsoft", "scim"] → "Google, Microsoft, or SCIM"
const PROVIDER_DISPLAY_NAMES = {
  scim: 'SCIM',
  github: 'GitHub',
  google: 'Google',
  microsoft: 'Microsoft',
  okta: 'Okta',
};

function toProviderDisplayName(providerId) {
  if (!providerId || typeof providerId !== 'string') return '';
  const normalized = providerId.trim().toLowerCase();
  if (PROVIDER_DISPLAY_NAMES[normalized]) return PROVIDER_DISPLAY_NAMES[normalized];
  return normalized
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatProviderListForLocale(providerIds, locale = 'en') {
  if (!providerIds || !Array.isArray(providerIds) || providerIds.length === 0) return null;
  const names = providerIds.map(toProviderDisplayName).filter(Boolean);
  if (names.length === 0) return '';
  const formatter = new Intl.ListFormat(locale, { style: 'long', type: 'disjunction' });
  return formatter.format(names);
}

function LoginWidget(props) {
  const email = useRef();
  const password = useRef();
  const lastCheckedEmailRef = useRef(''); // Use ref to avoid dependency issues
  const onEmailBlurRef = useRef();
  const { formatMessage, locale } = useIntl();
  const [passwordValue, setPasswordValue] = useState('');
  const dispatch = useDispatch();

  const { errorMessage, isSignup, onLoginError } = props;
  const [enforcedSSO, setEnforcedSSO] = useState(false);
  const [reason, setReason] = useState('');
  const [providers, setProviders] = useState([]);
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(true);
  const [attemptingPasskey, setAttemptingPasskey] = useState(false);

  const providersText = useMemo(
    () =>
      formatProviderListForLocale(providers, locale || 'en') ||
      formatMessage(loginMessages.yourSSOProvider),
    [providers, locale, formatMessage],
  );

  const handleSubmit = () => {
    if (enforcedSSO) {
      onLoginError(
        formatMessage(
          reason === 'Deprecated account'
            ? loginMessages.deprecatedAccount
            : loginMessages.ssoRequiredError,
          { providers: providersText },
        ),
      );
      return;
    }
    // Only check password strength for signup
    if (props.isSignup && !validatePassword(passwordValue, formatMessage).isStrong) {
      onLoginError(
        formatMessage({
          id: 'AuthError.WeakPasswordSignup',
          defaultMessage: 'Password is too weak. Please ensure it meets all requirements.',
        }),
      );
      return;
    }

    // Safely get values with optional chaining
    const emailValue = email.current?.getValue?.();
    const passwordValueFromInput = password.current?.getValue?.();

    // Defensive check - this should not happen due to form validation
    if (!emailValue || !passwordValueFromInput) {
      console.error('Form submission with undefined values');
      return;
    }

    const creds = {
      email: emailValue.trim().toLowerCase(),
      password: passwordValueFromInput.trim(),
    };
    props.onLoginClick(creds, props.isSignup);
  };

  const handlePasswordChange = useCallback(
    (name, value) => {
      setPasswordValue(value);

      // If password gets filled and we haven't checked for passkeys yet, check now
      if (value && !lastCheckedEmailRef.current) {
        const emailValue = email.current?.getValue?.();
        if (emailValue) {
          debouncedCheckPasskeys(emailValue);
        }
      }
    },
    [debouncedCheckPasskeys],
  ); // Remove lastCheckedEmail dependency

  const onGoogleSignIn = response => {
    props.onLoginClick({ credential: response.credential }, props.isSignup);
  };
  const onGoogleSignInError = error => {
    onLoginError(error.error);
  };

  // Check SSO policy on email blur and show passkey option if available
  const onEmailBlur = async () => {
    try {
      const value = email.current?.getValue?.();
      if (!value) {
        setHasPasskeys(false);
        setShowPasswordField(true);
        return;
      }

      // Skip if we've already checked this email
      if (value === lastCheckedEmailRef.current) {
        return;
      }

      // Check SSO policy first
      const json = await dispatch(checkAuthPolicy(value));
      setEnforcedSSO(!!json.enforced);
      setProviders(json.providers || []);
      setReason(json.reason);

      // If SSO is enforced or this is a signup, don't show passkey option
      if (json.enforced || props.isSignup) {
        setHasPasskeys(false);
        setShowPasswordField(true);
        lastCheckedEmailRef.current = value; // Update ref as well
        return;
      }

      // Check if user has passkeys registered for this email
      if (window.PublicKeyCredential && typeof window.PublicKeyCredential === 'function') {
        try {
          const passkeyResponse = await dispatch(checkPasskeys(value));
          if (passkeyResponse.hasPasskeys) {
            setHasPasskeys(true);
            setShowPasswordField(false); // Hide password field initially for passkey flow
            // Focus the login button after determining passkey availability
            // Find the login button by ref or querySelector and focus it
            setTimeout(() => {
              const loginButton = document.querySelector('button[type="submit"]');
              if (loginButton) {
                loginButton.focus();
              }
            }, 100);
          } else {
            setHasPasskeys(false);
            setShowPasswordField(true);
          }
          lastCheckedEmailRef.current = value; // Update ref as well
        } catch (passkeyError) {
          // If passkey check fails, fallback to password
          console.debug('Passkey check failed:', passkeyError.message);
          setHasPasskeys(false);
          setShowPasswordField(true);
          lastCheckedEmailRef.current = value; // Update ref as well
        }
      } else {
        setHasPasskeys(false);
        setShowPasswordField(true);
        lastCheckedEmailRef.current = value; // Update ref as well
      }
    } catch (e) {
      console.error('Email blur error:', e);
      setHasPasskeys(false);
      setShowPasswordField(true);
      // Don't update lastCheckedEmail on general errors, so it can be retried
    }
  };
  onEmailBlurRef.current = onEmailBlur;

  const buttonLabel = props.isSignup
    ? formatMessage(loginMessages['Signup.Submit'])
    : formatMessage(loginMessages['Login.Submit']);

  // Handle passkey authentication
  const handlePasskeyLogin = async e => {
    e.preventDefault();
    e.stopPropagation();
    const emailValue = email.current?.getValue?.();
    if (!emailValue) return;

    setAttemptingPasskey(true);
    try {
      const response = await dispatch(authenticatePasskey(emailValue));
      if (response.user) {
        // Successful passkey authentication - trigger login success
        props.onLoginClick({ passkeyAuthenticated: true, user: response.user }, false);
      }
    } catch (passkeyError) {
      // Passkey authentication failed - show error and fallback to password
      onLoginError(passkeyError.message || 'Passkey authentication failed');
      setShowPasswordField(true);
    } finally {
      setAttemptingPasskey(false);
    }
  };

  // Show password field instead of passkey
  const handleUsePasswordInstead = () => {
    setShowPasswordField(true);
  };

  // Debounced function to check passkeys when email changes
  const debouncedCheckPasskeys = useMemo(
    () =>
      debounce(async emailValue => {
        if (!emailValue) return;

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) return;

        // Skip if we've already checked this email
        if (emailValue === lastCheckedEmailRef.current) return;

        await onEmailBlurRef.current();
      }, 500),
    [],
  );

  // Check for autofilled values on mount
  useEffect(() => {
    const checkForAutofill = () => {
      const emailValue = email.current?.getValue?.();
      const passwordValue = password.current?.getValue?.();

      // If both fields have values, it's likely a password manager autofill
      if (emailValue && passwordValue && !lastCheckedEmailRef.current) {
        debouncedCheckPasskeys(emailValue);
      }
    };

    // Check after a short delay to allow password managers to fill the form
    const timer = setTimeout(checkForAutofill, 100);
    return () => clearTimeout(timer);
  }, [debouncedCheckPasskeys]); // Remove lastCheckedEmail dependency

  // Reset cached email check when user types in email field
  const handleEmailChange = () => {
    const currentValue = email.current?.getValue?.();
    if (currentValue !== lastCheckedEmailRef.current) {
      // Reset UI state when email changes
      setHasPasskeys(false);
      setShowPasswordField(true);
      setEnforcedSSO(false);
      setProviders([]);
      setReason('');
      lastCheckedEmailRef.current = ''; // Reset ref as well

      // Check for passkeys if email looks valid
      if (currentValue) {
        debouncedCheckPasskeys(currentValue);
      }
    }
  };

  return (
    <Box sx={loginWidgetContainerSx}>
      <Card
        sx={{
          maxWidth: 'md',
          p: {
            xs: 0,
            md: 2,
          },
          m: 1,
        }}
      >
        <CardHeader
          title={
            isSignup
              ? formatMessage(loginMessages['Signup.Title'])
              : formatMessage(loginMessages['Login.Title'])
          }
          titleTypographyProps={{
            sx: { fontWeight: '500' },
          }}
        />
        {errorMessage ? (
          <Message
            type="error"
            msg={errorMessage}
            onClose={props.onCloseError}
            sticky={false}
          />
        ) : null}
        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={loginWidgetFormSx}>
            <Formsy.Form onValidSubmit={handleSubmit}>
            <FormsyText
              variant="outlined"
              size="small"
              id="email"
              name="email"
              validations="isEmail"
              ref={email}
              label={formatMessage(loginMessages.email)}
              validationError={formatMessage(loginMessages.invalidEmail)}
              placeholder={formatMessage(loginMessages.emailPlaceholder)}
              autoFocus
              fullWidth
              onBlur={onEmailBlur}
              onChange={handleEmailChange}
              onInput={handleEmailChange}
              InputLabelProps={{
                shrink: true,
                required: false,
              }}
              sx={{ mb: 1 }}
            />
            {!enforcedSSO && showPasswordField && (
              <FormsyPassword
                variant="outlined"
                size="small"
                id="password"
                name="password"
                type="password"
                ref={password}
                label={formatMessage(loginMessages.password)}
                fullWidth
                onChange={handlePasswordChange}
                disabled={attemptingPasskey}
                InputLabelProps={{
                  shrink: true,
                  required: false,
                }}
              />
            )}
            {attemptingPasskey && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {formatMessage(loginMessages.passkeyLoginPrompt)}
              </Alert>
            )}
            {!enforcedSSO && hasPasskeys && !showPasswordField && !attemptingPasskey && (
              <Box sx={{ textAlign: 'center', my: 2 }}>
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleUsePasswordInstead}
                  sx={{ textDecoration: 'none', cursor: 'pointer' }}
                >
                  {formatMessage(loginMessages.usePasswordInstead)}
                </MuiLink>
              </Box>
            )}

            {/* Password Strength Indicator - Only for Signup */}
            {isSignup && !enforcedSSO ? (
              <PasswordStrengthIndicator password={passwordValue} marginTop={1} marginBottom={2} />
            ) : (
              showPasswordField &&
              !enforcedSSO && (
                <Box component={Link} sx={loginWidgetForgotSx} to="/forgot-password">
                  <FormattedMessage
                    id="Login.ForgotPassword"
                    defaultMessage="Forgot your password?"
                  />
                </Box>
              )
            )}
            {!enforcedSSO && (
              <Button
                variant="contained"
                sx={loginWidgetSubmitSx}
                type="submit"
                color="primary"
                style={{ width: '100%' }}
                onClick={hasPasskeys && !showPasswordField ? handlePasskeyLogin : undefined}
                disabled={
                  attemptingPasskey ||
                  (isSignup &&
                    passwordValue &&
                    !validatePassword(passwordValue, formatMessage).isStrong)
                }
              >
                {hasPasskeys && !showPasswordField
                  ? formatMessage(loginMessages.signInWithPasskey)
                  : buttonLabel}
              </Button>
            )}
            {enforcedSSO && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {formatMessage(
                  reason === 'Deprecated account'
                    ? loginMessages.deprecatedAccount
                    : loginMessages.ssoEnforced,
                  { providers: providersText },
                )}
              </Alert>
            )}
            </Formsy.Form>
          </Box>
          {enforcedSSO ? (
            <br />
          ) : (
            <Box
              component="hr"
              sx={loginWidgetHrTextSx}
              data-content={formatMessage(loginMessages.or)}
            />
          )}
          {!!process.env.CLIENT && locale && (
            <GoogleLogin
              onSuccess={onGoogleSignIn}
              onError={onGoogleSignInError}
              locale={locale}
              useOneTap
              auto_select
              cancel_on_tap_outside
              use_fedcm_for_prompt={fedcmSupported}
              use_fedcm_for_button={fedcmActiveMode}
              itp_support
              theme="filled_blue"
              logo_alignment="center"
              shape="rectangular"
              size="large"
              context={isSignup ? 'signup' : 'signin'}
              text={isSignup ? 'signup_with' : 'signin_with'}
            />
          )}
          {/* Terms and Privacy Agreement - Only for Signup */}
          {isSignup && (
            <Box sx={loginWidgetAcceptTermsSx}>
              <FormattedMessage
                id="Home.AgreeTerms"
                defaultMessage="By creating an account you agree to our {terms} & {privacy}."
                values={{
                  terms: (
                    <Link to={{ pathname: '/legal/terms' }}>
                      <FormattedMessage id="Home.Terms" defaultMessage="Terms" />
                    </Link>
                  ),
                  privacy: (
                    <Link to={{ pathname: '/legal/privacy' }}>
                      <FormattedMessage id="Home.Privacy" defaultMessage="Privacy Policy" />
                    </Link>
                  ),
                }}
              />
            </Box>
          )}
          {props.showSignupLink && !enforcedSSO && (
            <Typography sx={{ mt: 5, mb: 2 }}>
              <FormattedMessage
                id="LoginPage.GoToSignup"
                defaultMessage="Don't have an acount? Go to {signup}"
                values={{
                  signup: (
                    <Link to="/sign-up">
                      <FormattedMessage id="LoginPage.SignUp" defaultMessage="sign up" />
                    </Link>
                  ),
                }}
              />
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

LoginWidget.defaultProps = {
  errorMessage: null,
  showSignupLink: false,
  messagesPortalRef: null,
};

LoginWidget.propTypes = {
  onLoginClick: PropTypes.func.isRequired,
  errorMessage: PropTypes.shape({
    id: PropTypes.string,
  }),
  isSignup: PropTypes.bool.isRequired,
  onCloseError: PropTypes.func.isRequired,
  onLoginError: PropTypes.func.isRequired,
  showSignupLink: PropTypes.bool,
  messagesPortalRef: PropTypes.ref,
};

export default LoginWidget;
